"use client";

/**
 * VoiceNoteButton — the microphone button in the prompt input footer.
 *
 * **UX flow:**
 *   1. Click the mic button to start recording.
 *   2. While recording, the mic button is replaced by:
 *      - **Discard** (trash icon) — aborts the recording, produces no file.
 *      - **Audio waveform** — visual feedback of the live audio levels.
 *      The **submit** action is handled by the main chat submit button
 *      (`PromptInputSubmit`), which swaps to an arrow-up icon while
 *      recording is active.  This gives the user an explicit visual signal
 *      that the button's meaning has changed: the same button that normally
 *      sends a text message now sends the voice note.
 *
 * Recording is delegated to the pure-JS `VoiceRecorderSession`
 * (see `@/lib/voice-recorder`) so the lifecycle is unit-testable without
 * a DOM harness.  This component is just glue:
 *
 *   - MIME feature-detect at mount → render the button disabled if the
 *     browser doesn't expose a usable MediaRecorder.
 *   - On click (idle): spin up a `VoiceRecorderSession` and call `start()`.
 *   - On discard: call `abort()` — releases the mic, produces no file.
 *   - On submit: the parent calls the imperative `submitVoiceNote()` ref
 *     method, which calls `stop()`, feeds the produced `File` to
 *     {@link onSubmitVoiceNote} along with any text in the textarea.
 *   - On unmount: abort any in-flight recording so the browser's mic
 *     indicator turns off when the user navigates away.
 *
 * The button stays inside a `<PromptInput>`/`<PromptInputProvider>` so
 * the text-input controller is available for reading/clearing the
 * textarea content on voice-note submit.
 */

import { AudioWaveform } from "@/components/ai-elements/audio-waveform";
import {
  PromptInputButton,
  useOptionalPromptInputController,
} from "@/components/ai-elements/prompt-input";
import { MicIcon, MicOffIcon, Trash2Icon } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  defaultIsTypeSupported,
  selectVoiceMime,
  VoiceRecorderSession,
  type VoiceRecorderErrorCode,
} from "@/lib/voice-recorder";
import type { FileUIPart } from "ai";

type Phase = "unsupported" | "idle" | "recording" | "starting" | "stopping";

/** Map each phase to its i18n translation key. */
const PHASE_TOOLTIP_KEY: Record<Phase, string> = {
  unsupported: "voice.unsupportedBrowser",
  idle: "voice.recordVoiceNote",
  recording: "voice.stopRecording",
  starting: "voice.startingMic",
  stopping: "voice.finishingRecording",
};

/** Map each error code to its i18n translation key. */
const ERROR_LABEL_KEY: Record<VoiceRecorderErrorCode, string> = {
  permission_denied: "voice.micDenied",
  no_audio_track: "voice.noAudioTrack",
  recorder_failed: "voice.recordingFailed",
  unsupported: "voice.unsupportedShort",
};

/**
 * Imperative handle exposed via `ref` so the parent can trigger the
 * voice-note submit action from the main `PromptInputSubmit` button.
 */
export interface VoiceNoteButtonHandle {
  /** True when recording is in progress.  Parents can poll this to decide
   *  whether the submit button should act as a voice-note send. */
  isRecording: () => boolean;
  /** Stop the recording and submit the voice note (calls `onSubmitVoiceNote`).
   *  No-op if not currently recording. */
  submitVoiceNote: () => void;
  /** Start the same recording flow as clicking the microphone button.
   *  No-op while unavailable, disabled, or already recording. */
  startRecording: () => void;
}

export type VoiceNoteButtonProps = {
  /** Override the tooltip text when the button is idle and no error is
   *  shown.  We keep "Voice note" generic so the button works
   *  in either a footer or an action menu. */
  tooltip?: string;
  /** Disable the button entirely (e.g. during streaming). */
  disabled?: boolean;
  /**
   * Called when the voice note is submitted (via the imperative ref or
   * a fallback inline submit button).  Receives the current textarea text
   * (may be empty), the recorded audio `File`, and any existing attachment
   * files that were already staged in the prompt input (e.g. images the
   * user attached before starting the recording).  The parent should send
   * the message immediately — the audio will be transcribed server-side
   * by `sendMessage`.
   *
   * If not provided, the submit button is hidden and the component
   * falls back to the legacy "stop → attach" behaviour (useful for
   * testing or non-chat contexts).
   */
  onSubmitVoiceNote?: (
    text: string,
    file: File,
    existingFiles: FileUIPart[],
  ) => void;
  /**
   * Notified whenever the recording state changes.  The parent uses this
   * to swap the main submit button into "voice-note send" mode.
   */
  onRecordingChange?: (recording: boolean) => void;
};

export const VoiceNoteButton = forwardRef<
  VoiceNoteButtonHandle,
  VoiceNoteButtonProps
>(function VoiceNoteButton(
  { tooltip, disabled = false, onSubmitVoiceNote, onRecordingChange },
  ref,
) {
  const { t } = useTranslation();
  const resolvedTooltip = tooltip ?? t("voice.recordVoiceNote");
  const controller = useOptionalPromptInputController();

  // MIME is feature-detected once at mount — it's a property of the
  // browser, not the user action.  If no candidate works, the button
  // renders permanently disabled with an explanatory tooltip.
  const mime = useMemo(() => selectVoiceMime(defaultIsTypeSupported), []);

  const [phase, setPhase] = useState<Phase>(mime ? "idle" : "unsupported");
  const [error, setError] = useState<string | null>(null);
  // The live MediaStream, exposed for the AudioWaveform visualiser.
  // Set when recording starts, cleared on stop/discard/unmount.
  const [stream, setStream] = useState<MediaStream | null>(null);

  // Session is held in a ref so React state updates don't tear down an
  // in-progress recording.  We assign lazily on the first click and
  // null it out only on unmount or after a successful stop.
  const sessionRef = useRef<VoiceRecorderSession | null>(null);

  // Keep a ref mirror of the recording state so the imperative handle
  // can report it without a stale closure.
  const recordingRef = useRef(false);
  useEffect(() => {
    recordingRef.current = phase === "recording";
  }, [phase]);

  // Notify parent whenever the recording state changes.
  useEffect(() => {
    onRecordingChange?.(phase === "recording");
  }, [phase, onRecordingChange]);

  const handleError = useCallback(
    (code: VoiceRecorderErrorCode, message: string) => {
      console.warn(`[ui] Voice recorder error (${code}): ${message}`);
      setError(t(ERROR_LABEL_KEY[code] ?? "", { defaultValue: message }));
      sessionRef.current = null;
      setStream(null);
      setPhase("idle");
    },
    [t],
  );

  const handleStart = useCallback(async () => {
    if (!mime || disabled || phase !== "idle" || sessionRef.current) return;
    setError(null);
    setPhase("starting");
    const session = new VoiceRecorderSession({
      mime,
      onError: handleError,
    });
    sessionRef.current = session;
    await session.start();
    // After start(): either the recorder is running ("recording"), or it
    // already errored (handleError ran and cleared sessionRef).  Check
    // the session ref so we don't clobber an error-induced "idle" with
    // a "recording" write — a bug I caught during implementation.
    if (sessionRef.current === session) {
      if (!session.isRecording) {
        // Shouldn't normally happen, since onError nulls the ref.  But
        // if some other path put the session into a terminal state
        // without firing onError, fall back to "idle".
        sessionRef.current = null;
        setPhase("idle");
      } else {
        setStream(session.stream);
        setPhase("recording");
      }
    }
    // If sessionRef !== session, onError already ran and set phase to
    // "idle" — leave it alone.
  }, [disabled, handleError, mime, phase]);

  // ── Discard: abort the recording, no file produced ──────────────────
  const handleDiscard = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    session.abort();
    sessionRef.current = null;
    setStream(null);
    setPhase("idle");
    setError(null);
  }, []);

  // ── Submit: stop recording, send the voice note immediately ─────────
  const handleSubmitVoiceNote = useCallback(async () => {
    const session = sessionRef.current;
    if (!session) return;
    setPhase("stopping");
    try {
      const result = await session.stop();
      if (result && onSubmitVoiceNote) {
        // Read the current textarea text (may be empty) and clear it
        // so the textarea is ready for the next message.
        const text = controller?.textInput.value ?? "";
        controller?.textInput.clear();
        // Gather any existing attachment files that were already staged
        // (e.g. images the user attached before starting the recording).
        // The voice-note submit bypasses PromptInput's onSubmit, so those
        // attachments would otherwise be lost.  We pass them to the parent
        // and clear the attachment list here.
        const existingFiles = controller
          ? controller.attachments.files.map(({ id: _id, ...rest }) => rest)
          : [];
        if (existingFiles.length > 0) {
          controller?.attachments.clear();
        }
        onSubmitVoiceNote(text, result.file, existingFiles);
      }
    } finally {
      sessionRef.current = null;
      setStream(null);
      setPhase("idle");
      setError(null);
    }
  }, [onSubmitVoiceNote, controller]);

  // ── Imperative handle: let the parent trigger submit + check state ──
  useImperativeHandle(
    ref,
    (): VoiceNoteButtonHandle => ({
      isRecording: () => recordingRef.current,
      submitVoiceNote: () => void handleSubmitVoiceNote(),
      startRecording: () => void handleStart(),
    }),
    [handleStart, handleSubmitVoiceNote],
  );

  // Abort any in-flight recording when the component unmounts so the
  // browser's mic-on indicator turns off and tracks are released.  No
  // resume-on-remount behaviour — recordings are intentionally not
  // persisted across remounts.
  useEffect(() => {
    return () => {
      sessionRef.current?.abort();
      sessionRef.current = null;
      setStream(null);
    };
  }, []);

  // ── Idle / unsupported / starting / stopping: single mic button ─────
  if (phase !== "recording") {
    const Icon = phase === "unsupported" ? MicOffIcon : MicIcon;
    const tooltipText =
      phase === "idle" && error
        ? error
        : phase === "idle"
          ? resolvedTooltip
          : t(PHASE_TOOLTIP_KEY[phase]);

    return (
      <PromptInputButton
        aria-label={tooltipText}
        disabled={
          disabled ||
          phase === "unsupported" ||
          phase === "starting" ||
          phase === "stopping"
        }
        tooltip={tooltipText}
        data-testid="voice-note-button"
        data-recording={false}
        onClick={handleStart}
      >
        <Icon className="size-4" />
      </PromptInputButton>
    );
  }

  // ── Recording: show discard + waveform ──────────────────────────────
  // The submit action is handled by the main PromptInputSubmit button
  // (via the imperative ref).  We no longer render an inline submit
  // button here — the submit button's icon changes to ArrowUpIcon while
  // recording to signal that it now sends the voice note.
  return (
    <div className="flex items-center gap-1" data-testid="voice-note-recording-controls">
      <PromptInputButton
        aria-label={t("voice.discard", { defaultValue: "Discard recording" })}
        tooltip={t("voice.discard", { defaultValue: "Discard recording" })}
        data-testid="voice-note-discard"
        onClick={handleDiscard}
      >
        <Trash2Icon className="size-4 text-muted-foreground" />
      </PromptInputButton>
      <AudioWaveform stream={stream} bars={5} maxHeight={20} className="px-1" />
    </div>
  );
});
